import React, { useState } from 'react';

function ChoiceDistribution({ q }) {
  return (
    <div className="space-y-1.5">
      {q.multiple && <p className="text-[11px] text-slate-400">Mehrfachauswahl — Prozente summieren sich nicht zu 100 %.</p>}
      {q.distribution.map(d => (
        <div key={d.option}>
          <div className="flex justify-between text-xs text-slate-600 dark:text-slate-300">
            <span>{d.option}</span>
            <span>{d.count} · {d.pct} %</span>
          </div>
          <div className="h-2 rounded bg-slate-100 dark:bg-slate-700 overflow-hidden">
            <div className="h-full rounded" style={{ width: `${Math.min(d.pct, 100)}%`, background: '#0891b2' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function FreeTextExplorer({ q }) {
  const [term, setTerm] = useState('');
  const needle = term.trim().toLowerCase();
  const showSearch = q.textAnswers.length > 8;
  const filtered = needle ? q.textAnswers.filter(a => a.toLowerCase().includes(needle)) : q.textAnswers;
  return (
    <div>
      {/* Only surface "häufigste Antworten" when answers actually repeat — for
          unique free-text (e.g. names) every count is 1 and the badges would
          just duplicate the list below. */}
      {!needle && q.topAnswers.some(t => t.count > 1) && (
        <div className="mb-3">
          <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mb-2">Häufigste Antworten</div>
          <div className="flex flex-wrap gap-2">
            {q.topAnswers.filter(t => t.count > 1).map(t => (
              <span key={t.value} className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 px-2 py-1 rounded-full">
                {t.value} · {t.count}
              </span>
            ))}
          </div>
        </div>
      )}
      {showSearch && (
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Antworten durchsuchen …"
          className="w-full mb-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-3 py-2"
        />
      )}
      <ul className="max-h-48 overflow-y-auto space-y-1 text-sm text-slate-700 dark:text-slate-200">
        {filtered.map((a, i) => <li key={i} className="border-b border-slate-100 dark:border-slate-800 py-1">{a}</li>)}
        {filtered.length === 0 && <li className="text-slate-400 py-1">Keine Antworten gefunden.</li>}
      </ul>
    </div>
  );
}

function RankingDistribution({ q }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] text-slate-400 mb-1">Sortiert nach durchschnittlicher Platzierung (1 = höchste Priorität).</p>
      {q.ranking.map((r, i) => (
        <div key={r.option} className="flex justify-between text-xs text-slate-600 dark:text-slate-300 py-0.5">
          <span>{i + 1}. {r.option}</span>
          <span className="text-slate-400">Ø {r.avgRank} · {r.count}×</span>
        </div>
      ))}
    </div>
  );
}

export function AnswerAnalysis({ options = [], selectedId, onSelect, questions }) {
  const hasOptions = options.length > 0;
  return (
    <div>
      <div className="mb-4">
        <label htmlFor="answer-fragebogen" className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
          Fragebogen für die Analyse
        </label>
        <select
          id="answer-fragebogen"
          aria-label="Fragebogen für die Analyse"
          value={selectedId || ''}
          onChange={(e) => onSelect && onSelect(e.target.value)}
          disabled={!hasOptions}
          className="w-full max-w-md rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100 px-3 py-2 disabled:opacity-60"
        >
          <option value="">— Fragebogen wählen —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {(o.title || 'Unbenannt')} · {o.count} {o.count === 1 ? 'Ergebnis' : 'Ergebnisse'}
            </option>
          ))}
        </select>
      </div>

      {!hasOptions ? (
        <div className="text-sm text-slate-400 py-6 text-center">Keine Fragebögen mit Ergebnissen im Zeitraum.</div>
      ) : !selectedId ? (
        <div className="text-sm text-slate-400 py-6 text-center">Wähle oben einen Fragebogen, um die Antworten je Frage zu sehen.</div>
      ) : !questions || questions.length === 0 ? (
        <div className="text-sm text-slate-400 py-6 text-center">Keine Fragen mit Antworten für diesen Fragebogen.</div>
      ) : (
        <div className="space-y-6">
          {questions.map((q) => (
            <div key={q.id} className="border-b border-slate-100 dark:border-slate-800 pb-4 last:border-0">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="text-sm font-medium text-slate-800 dark:text-slate-100">{q.text}</h4>
                <span className="text-xs text-slate-400 whitespace-nowrap">{q.skipRatePct} % übersprungen</span>
              </div>
              {(q.type === 'single-choice' || q.type === 'multiple-choice' || q.type === 'matrix' || q.type === 'combobox') && <ChoiceDistribution q={q} />}
              {q.type === 'ranking' && <RankingDistribution q={q} />}
              {q.type === 'text' && <FreeTextExplorer q={q} />}
              {!['single-choice', 'multiple-choice', 'matrix', 'combobox', 'ranking', 'text'].includes(q.type) && (
                <p className="text-xs text-slate-400">{q.answeredCount} Antworten (Typ „{q.type}" ohne Detailauswertung).</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
